/**
 * Read + override/revert access to the KPI tables (KPI workflow MVP).
 *
 * Powers `/kpi`: the per-property grid, the portfolio rollup, and the manual
 * override (and its revert) behind the drill-in panel. All network access goes through
 * `apiFetch` (never raw `fetch`); responses are validated at the boundary and a
 * bad payload degrades to `null` so the page shows an error state rather than
 * rendering against an unknown shape.
 */

import {
	kpiPortfolioFixture,
	kpiTableFixture,
} from "../fixtures/kpiTable.fixture";
import {
	type KpiOverrideResponse,
	type KpiPeriodGrain,
	type KpiTable,
	parseKpiOverrideResponse,
	parseKpiTable,
} from "../types/kpi";
import { apiFetch } from "../utils/apiClient";

/**
 * What happened to an override attempt.
 *
 * A save that failed because the row was SUPERSEDED is a different event from
 * one that failed because the network dropped: the first means the number the
 * user was looking at is no longer current, and the honest response is to show
 * them what replaced it — not to invite them to press Save again, which would
 * fail identically forever.
 */
/** The row was superseded between the read and the save. */
const HTTP_CONFLICT = 409;
/** The account has no KPI scope (no client association) — nothing to read. */
const HTTP_FORBIDDEN = 403;

export type KpiOverrideOutcome =
	| { kind: "saved"; response: KpiOverrideResponse }
	| { kind: "conflict" }
	| { kind: "failed" };

/**
 * What happened to a revert attempt — the same three outcomes as an override.
 * A 409 here means the row is no longer OVERRIDDEN (a newer extraction
 * superseded it, or it was already reverted), which is a different event from
 * "could not reach the server": the first means the number the user was
 * looking at is stale, and the honest response is to show them the current
 * value — not to invite a retry that would fail identically forever.
 */
/**
 * What came back for a table read.
 *
 * `unavailable` is the honest answer to a 403: the server did not fail, it
 * said this account has no KPI scope to read (no client association, so no
 * portfolio). That is an EMPTY dashboard, not a broken one — collapsing it
 * into `failed` puts a red "could not load" banner and a Retry button in front
 * of a user whose retry can only produce the same 403.
 */
export type KpiTableOutcome =
	| { kind: "ok"; table: KpiTable }
	| { kind: "unavailable" }
	| { kind: "failed" };

export type KpiRevertOutcome =
	| { kind: "saved"; response: KpiOverrideResponse }
	| { kind: "conflict" }
	| { kind: "failed" };

export interface KpiTableQuery {
	/** Omit together with `rollup: true` is invalid; the page always sets one. */
	propertyKey?: string;
	rollup?: boolean;
	/**
	 * Period bucket to aggregate into. Omitted means the server's default
	 * ("month"), which is also what a pre-grain server does with the parameter
	 * it does not know — the request stays valid either way.
	 */
	grain?: KpiPeriodGrain;
	/** `YYYY-MM-DD`, inclusive. */
	from?: string;
	to?: string;
	/**
	 * Public id of the workflow whose dashboard this is. Scopes the grid to
	 * that workflow's values (plus the hand-entered ones, which belong to every
	 * dashboard covering their metric); omitted reads every value the user has.
	 */
	workflowId?: string;
}

/**
 * Dev-only fixture mode: `http://localhost:3000/kpi?fixture=1` renders the
 * synthetic KPI fixture without touching the API, so the dashboard can be built
 * and reviewed before T1's router lands.
 *
 * Two guards keep this out of production: `NODE_ENV === "development"` (CRA
 * hard-codes `"production"` in `npm run build`, so the branch is dead code that
 * gets stripped) and the explicit opt-in query param. It short-circuits BEFORE
 * the request rather than acting as a fallback, so it can never mask a live API
 * error. (Same shape as `cabinetService`.)
 */
function shouldUseFixture(): boolean {
	if (process.env.NODE_ENV !== "development") return false;
	if (typeof window === "undefined") return false;
	return new URLSearchParams(window.location.search).get("fixture") === "1";
}

function buildQuery(query: KpiTableQuery): string {
	const params = new URLSearchParams();
	if (query.rollup) params.set("rollup", "portfolio");
	else if (query.propertyKey) params.set("property_key", query.propertyKey);
	if (query.grain) params.set("grain", query.grain);
	if (query.from) params.set("from", query.from);
	if (query.to) params.set("to", query.to);
	if (query.workflowId) params.set("workflow_id", query.workflowId);
	const encoded = params.toString();
	return encoded ? `?${encoded}` : "";
}

class KpiService {
	/**
	 * Fetch one grid — a property's, or the portfolio rollup.
	 *
	 * Reports WHICH non-answer happened rather than collapsing every one to
	 * null: a 403 means there is no KPI scope for this account (nothing to
	 * show), which the dashboard renders as an empty state, while a transport
	 * or shape failure is a real error the user can retry.
	 */
	async getTable(query: KpiTableQuery): Promise<KpiTableOutcome> {
		if (shouldUseFixture()) {
			return {
				kind: "ok",
				table: query.rollup
					? kpiPortfolioFixture()
					: kpiTableFixture(query.propertyKey ?? "Northwind Flats"),
			};
		}
		try {
			const resp = await apiFetch(`/api/kpi/table${buildQuery(query)}`);
			if (resp.status === HTTP_FORBIDDEN) return { kind: "unavailable" };
			if (!resp.ok) return { kind: "failed" };
			const table = parseKpiTable(await resp.json());
			return table === null ? { kind: "failed" } : { kind: "ok", table };
		} catch {
			return { kind: "failed" };
		}
	}

	/**
	 * Replace an extracted value with a user-supplied one.
	 *
	 * Reports WHICH failure happened rather than collapsing every one to null.
	 * A 409 means the server refused because a newer extraction superseded the
	 * row (T1's override supersession guard, codex R1/F5) — the user is editing
	 * a number that no longer exists, and telling them "could not save" would
	 * send them back to press Save on a row that can never accept it.
	 *
	 * `valueId` is the row's PUBLIC UUID, taken straight from the cell — the
	 * internal PK is never exposed, so there is nothing else it could be.
	 */
	async overrideValue(
		valueId: string,
		value: number,
	): Promise<KpiOverrideOutcome> {
		if (shouldUseFixture()) {
			return {
				kind: "saved",
				response: {
					id: valueId,
					override_value: value,
					status: "overridden",
					override_by: "fixture",
				},
			};
		}
		try {
			const resp = await apiFetch(
				`/api/kpi/values/${encodeURIComponent(valueId)}/override`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ value }),
				},
			);
			if (resp.status === HTTP_CONFLICT) return { kind: "conflict" };
			if (!resp.ok) return { kind: "failed" };
			const parsed = parseKpiOverrideResponse(await resp.json());
			// A 200 whose body does not match the contract is still a failure: the
			// save may have happened, but nothing here can say what it saved.
			return parsed === null
				? { kind: "failed" }
				: { kind: "saved", response: parsed };
		} catch {
			return { kind: "failed" };
		}
	}

	/**
	 * Undo a manual override: the extracted value is the answer again.
	 *
	 * Same three-outcome shape as `overrideValue`, and the same rationale for
	 * distinguishing them: a 409 means the row is no longer OVERRIDDEN — the
	 * number the user was looking at stopped being current — which is not a
	 * network failure and must not be retried blind. The response reuses
	 * `parseKpiOverrideResponse`: the envelope is identical, with `status`
	 * `"active"` and the override fields null.
	 */
	async revertValue(valueId: string): Promise<KpiRevertOutcome> {
		if (shouldUseFixture()) {
			return {
				kind: "saved",
				response: {
					id: valueId,
					override_value: null,
					status: "active",
					override_by: null,
				},
			};
		}
		try {
			const resp = await apiFetch(
				`/api/kpi/values/${encodeURIComponent(valueId)}/override`,
				{ method: "DELETE" },
			);
			if (resp.status === HTTP_CONFLICT) return { kind: "conflict" };
			if (!resp.ok) return { kind: "failed" };
			const parsed = parseKpiOverrideResponse(await resp.json());
			// A 200 whose body does not match the contract is still a failure: the
			// revert may have happened, but nothing here can say what it reverted.
			return parsed === null
				? { kind: "failed" }
				: { kind: "saved", response: parsed };
		} catch {
			return { kind: "failed" };
		}
	}
}

export const kpiService = new KpiService();
export default kpiService;
